import { useState } from "react";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import { BookOpen } from "@/design-system/icons/book-open";
import { BurgerMenu } from "@/design-system/icons/burger-menu";
import { GoToLink } from "@/design-system/icons/go-to-link";
import { useOutsideClick } from "@/lib/hooks/use-outside-click";
import Share from "@/src/common/Share";
import type { IShare } from "@/src/common/ShareDefinitions";
import { useViewportInfoContext } from "@/src/contexts";
import type { IFlyerCatalogueResources } from "@/src/flyers/FlyerCatalogue/FlyerCatalogue.Resources";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, colors } from "../../../styles/tokens.stylex";

export type IFlyerCatalogueModalShare = {
    brandName?: string;
    brandUrlSeo: string;
    share?: IShare;
    resources: IFlyerCatalogueResources;
    onModalClose: () => void;
    title: string;
    caption: string;
    shareUrl: string;
};

export default function FlyerCatalogueModalShare(props: IFlyerCatalogueModalShare) {
    const { isMobileOrTablet } = useViewportInfoContext();
    const containerRef = useOutsideClick<HTMLDivElement>(() => setActionMenuOpen(false));
    const [actionMenuOpen, setActionMenuOpen] = useState(false);

    const showFlyerPagesBtn = (menuStyles: stylex.StyleXStyles) => (
        <Button as="button" variant="none" size="None" onClick={props.onModalClose} styleX={menuStyles}>
            <span {...stylex.props(styles.actionIcon)}>
                <BookOpen />
            </span>
            <Typography textElement="span" fontStyle="BodyM" styleX={styles.actionLabel}>
                {props.resources.flyerPages}
            </Typography>
        </Button>
    );

    const goToBrandBtn = (menuStyles: stylex.StyleXStyles) => (
        <Button
            as="a"
            href={props.brandUrlSeo}
            variant="none"
            size="None"
            onClick={props.onModalClose}
            styleX={menuStyles}
        >
            <span {...stylex.props(styles.actionIcon)}>
                <GoToLink />
            </span>
            <Typography textElement="span" fontStyle="BodyM" styleX={styles.actionLabel}>
                {props.resources.navigateToBrand.replace("{brandName}", props.brandName)}
            </Typography>
        </Button>
    );

    const shareBtn = (menuStyles: stylex.StyleXStyles) =>
        props.shareUrl && (
            <div {...stylex.props(menuStyles)}>
                <Share
                    text={`${props.caption || ""}: ${props.title} ${props.shareUrl}`}
                    shareUrl={props.shareUrl}
                    title={`Goodie - ${props.title}`}
                    appearance="dark"
                    buttonStyleX={menuStyles}
                    iconStyleX={styles.actionIcon}
                    showTooltip
                >
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.actionLabel}>
                        {props.resources.share}
                    </Typography>
                </Share>
            </div>
        );

    if (isMobileOrTablet) {
        return (
            <div {...stylex.props(styles.actionWrapper)} ref={containerRef}>
                <Button
                    as="button"
                    size="None"
                    variant="none"
                    styleX={[styles.actionIcon, styles.actionLabel]}
                    onClick={() => setActionMenuOpen(() => !actionMenuOpen)}
                >
                    <BurgerMenu />
                </Button>
                <div {...stylex.props(styles.actionOptions, actionMenuOpen && styles.opened)}>
                    {showFlyerPagesBtn([styles.action, styles.actionOnMenu])}
                    {goToBrandBtn([styles.action, styles.actionOnMenu])}
                    {shareBtn([styles.action, styles.actionOnMenu])}
                </div>
            </div>
        );
    }

    return (
        <div {...stylex.props(styles.actionLinks)}>
            {showFlyerPagesBtn(styles.action)}
            {goToBrandBtn(styles.action)}
            {shareBtn(styles.action)}
        </div>
    );
}

// const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    actionLinks: {
        gridArea: "actionLink",
        gap: 20,
        alignItems: "center",
        display: "flex",
        justifyContent: "flex-end",
    },

    action: {
        gap: ComposableSpacing.s1,
        alignItems: "center",
        color: colors.fg_AltDefault,
        display: "flex",
        flexDirection: "column",
    },

    actionOnMenu: {
        gap: ComposableSpacing.s3,
        flexDirection: "row",
    },

    actionLabel: {
        color: colors.fg_AltDefault,
    },

    actionIcon: {
        height: 24,
        width: 24,
    },

    actionWrapper: {
        position: "relative",
    },

    actionOptions: {
        borderRadius: ComposableSpacing.s1,
        backgroundColor: "black",

        boxShadow: `0 0 ${ComposableSpacing.s1} ${colors.b_Tertiary}`,

        gap: 0,
        display: "grid",
        gridTemplateRows: "repeat(3, 0fr)",
        listStyleType: "none",
        opacity: 0,
        position: "absolute",
        transitionDuration: "0.3s",
        right: 0,
        top: "100%",
        transitionProperty: "grid-template-rows, opacity, gap",
        transitionTimingFunction: "ease",

        zIndex: 1,
        paddingBottom: ComposableSpacing.s2,
        width: "max-content",
        paddingLeft: ComposableSpacing.s4,
        paddingRight: ComposableSpacing.s4,
        paddingTop: ComposableSpacing.s2,
    },

    opened: {
        gap: "1.15rem",
        gridTemplateRows: "repeat(3, 1fr)",
        opacity: 1,
    },
});
